import { useEffect, useState } from "react";
import {
  IPaginationWithQueryString,
  ISynchronizePaginationWithApi,
} from "../interfaces-and-types";
import { filterConstants } from "../constants";
import { useQueryStringFilter } from "./useSetQueryStringFilter";
import { useGetPageQueryParams } from "./useGetPageQueryParams";
import { number } from "yup";

interface IInitialPaginationParams {
  queryStringFilterPrefix: string;
  initialPage?: number;
  initialPageSize?: number;
  initialSearch?: string | null;
}

const DEFAULT_PAGE_SIZE = 10;

const appendQueryStringPrefix = (
  queryStringFilterPrefix: string,
  queryKey: string
) =>
  `${
    !!queryStringFilterPrefix ? `${queryStringFilterPrefix}.` : ""
  }${queryKey}`;

export const usePaginationWithQueryString = (
  initialParams: IInitialPaginationParams
): IPaginationWithQueryString => {
  const {
    queryStringFilterPrefix,
    initialPage = 1,
    initialPageSize = DEFAULT_PAGE_SIZE,
    initialSearch,
  } = initialParams || {
    queryStringFilterPrefix: "",
    initialPage: 1,
    initialPageSize: DEFAULT_PAGE_SIZE,
    initialSearch: null,
  };

  const queryStringFilterPrefixValue = queryStringFilterPrefix ?? "";

  const [searchString, setSearchString] = useState<string | null>(
    initialSearch ?? null
  );
  const [currentPage, setCurrentPage] = useState(Number(initialPage));
  const [currentPageSize, setCurrentPageSize] = useState(
    Number(initialPageSize)
  );

  // get the values from the query string in the browser
  const { page, page_size, search } = useGetPageQueryParams({
    queryStringPrefix: queryStringFilterPrefix || "",
  });

  // set the values of the query strings on the browser
  const { setQueryStringFilter } = useQueryStringFilter([
    [
      appendQueryStringPrefix(
        queryStringFilterPrefixValue,
        filterConstants.searchKey
      ),
      initialSearch ?? "",
    ],
    [
      appendQueryStringPrefix(
        queryStringFilterPrefixValue,
        filterConstants.currentPageKey
      ),
      currentPage.toString(),
    ],
    [
      appendQueryStringPrefix(
        queryStringFilterPrefixValue,
        filterConstants.pageSizeKey
      ),
      currentPageSize.toString(),
    ],
  ]);

  // set current page on url when page changed in table
  useEffect(() => {
    setQueryStringFilter(
      appendQueryStringPrefix(
        queryStringFilterPrefixValue,
        filterConstants.currentPageKey
      ),
      currentPage.toString()
    );
  }, [currentPage]);

  // set page size on url
  useEffect(() => {
    setQueryStringFilter(
      appendQueryStringPrefix(
        queryStringFilterPrefixValue,
        filterConstants.pageSizeKey
      ),
      currentPageSize.toString()
    );
  }, [currentPageSize]);

  const syncWithApiResponse = ({
    apiResponsePage,
    apiResponsePageSize,
  }: // apiResponsePageCount,
  // APIRESPONSEITEMCOUNT,
  ISynchronizePaginationWithApi): void => {
    apiResponsePage !== currentPage && setCurrentPage(apiResponsePage);
    apiResponsePageSize !== currentPageSize &&
      setCurrentPageSize(apiResponsePageSize);
    // apiResponsePageCount !== PageCount && setCurrentPageCount(apiResponsePageCount);
    // apiResponseItemCount !== ItemCount && setCurrentItemCount(apiResponseItemCount);
  };

  return {
    currentPage,
    setCurrentPage,
    currentPageSize,
    setCurrentPageSize,
    syncWithApiResponse,
  };
};
